// Decorators
import { OnChange } from '@frontendlib/common/decorators/on-change';

// Types
import { Like } from '@modules/common/types/like';
import { AdvancedSearchState } from '@modules/search/types/advanced-search-state';
import { StateKey } from '@modules/settings/types/state-key';
import { type MessageFolder } from './message-folder';

// Services
import { BaseStitchListStateWithSearch } from '@modules/common/types/BaseStitchListStateWithSearch';
import { SortOrder } from '@modules/common/types/sort-order';
import { SortBy } from '@modules/common/types/SortBy';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { MailAppAdvancedFilters } from '@modules/search/types/mail-app-advanced-filters';
import { StateService } from '@modules/settings/services/state.service';

export type MessageFolderListSortField = 'title' | 'createdAt' | 'updatedAt' | 'score';

export class MessageFoldersListState extends BaseStitchListStateWithSearch<MessageFolder, MessageFolderListSortField> {
  @OnChange('onPropertyChange')
  declare public sortBy?: MessageFolderListSortField;
  @OnChange('onPropertyChange')
  declare public today?: boolean;

  constructor({
    searchService,
    stateService,
    stateKey,
    withAdvanced,
    defaultSort = { by: 'createdAt', order: SortOrder.DESC },
  }: {
    searchService?: AdvancedSearchService;
    stateService?: StateService;
    stateKey?: StateKey;
    withAdvanced?: boolean;
    defaultSort?: SortBy<MessageFolderListSortField>;
  }) {
    super({ searchService, stateService, stateKey, withAdvanced, defaultSort });
  }

  protected override applySavedState(state?: Like<MessageFoldersListState>): void {
    super.applySavedState(state);
    // Session-only, never restored: the toggle is drawn in KP alone now, and KP list states carry no
    // `stateKey`. So wherever a saved `true` could still come from, the control that set it is no longer
    // drawn there — restoring one would date-filter a list whose menu offers nothing to turn it back
    // off, leaving "Reset to default" as the only way out. Note this is NOT "the toggle never worked":
    // in four sidebars it did (calendars, groups, folders, notebooks), because their chain never runs
    // `applyAdvancedFilters` and their menus gate it on `withScore` alone. See
    // devdoc/specs/today-filter.md, "Why it is not persisted".
    this.today = false;
  }

  public override onPropertyChange(attribute: keyof this): void {
    if ((attribute === 'from' && this.from) || (attribute === 'to' && this.to)) {
      this.today = false;
    }

    if (attribute === 'today' && this.today) {
      this.from = null;
      this.to = null;
    }

    super.onPropertyChange(attribute);
  }

  protected override syncState() {
    super.syncState({
      sortBy: this.sortBy,
    });
  }

  public override isDefault(): boolean {
    return super.isDefault() && !this.today;
  }

  public override hasActiveFilters(): boolean {
    return super.hasActiveFilters() || !!this.today;
  }

  public override clearFilters(): void {
    this.today = false;
    super.clearFilters();
  }

  protected override syncFromAS({ mail: state }: AdvancedSearchState) {
    this.stitchedWith = state.stitched;
    this.from = state.dateRangeFrom;
    this.to = state.dateRangeTo;
  }

  protected override syncToAS(): void {
    const searchState = this.searchService.getStateSync();

    this.searchService.setState(
      new AdvancedSearchState({
        ...searchState,
        mail: new MailAppAdvancedFilters({
          ...searchState.mail,
          stitched: this.stitchedWith,
          dateRangeFrom: this.from,
          dateRangeTo: this.to,
        }),
      }),
    );
  }
}
